import { Button, Card, CircularProgress, Typography } from '@mui/material';
import clsx from 'clsx';
import { ErrorInline } from '../../errors/error-inline/error-inline.component';
import { Snackbar } from '../../snackbar/snackbar.component';
import { EntityHistoryTimelineProps } from '../entity-history-timeline.types';
import { EntityHistoryTimelineControls } from '../entity-history-timeline-controls/entity-history-timeline-controls.component';
import { EntityHistoryTimelineGroup } from '../entity-history-timeline-group/entity-history-timeline-group.component';
import { useEntityHistoryCardStyles } from './entity-history-timeline-card.styles';
import { PermissionBoundary } from '../../../../../core/permissions/permission-boundary.component';
import { useTextStyles } from '../../../../theme/text';

export function EntityHistoryTimelineCard({
  groups,
  isLoading,
  isError,
  pivotDate,
  setPivotDate,
  loadOlder,
  loadNewer,
  hasOlderEntries,
  isLoadingOlder,
  isLoadingNewer,
  noNewerEventsFound,
  clearNoNewerEvents,
  currentProjectID,
  permission,
  className
}: EntityHistoryTimelineProps) {
  const classes = useEntityHistoryCardStyles();
  const textClasses = useTextStyles();

  return (
    <PermissionBoundary permissions={[{ action: permission, projectID: currentProjectID }]}>
      <Card elevation={0} className={clsx(classes.card, className)}>
        {isError ? (
          <div className={classes.body}>
            <ErrorInline
              open={true}
              error={{
                title: 'Something went wrong',
                message: 'Sorry, we were unable to load the entity history. Please try again later.'
              }}
            />
          </div>
        ) : (
          <>
            <div className={classes.header}>
              <Typography className={textClasses.h2}>History</Typography>
              <EntityHistoryTimelineControls pivotDate={pivotDate} setPivotDate={setPivotDate} />
            </div>
            <div className={classes.body}>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.button}
                  variant="outlined"
                  onClick={loadNewer}
                  disabled={isLoadingNewer}
                  size="small"
                >
                  {isLoadingNewer ? 'Loading...' : 'Load newer'}
                </Button>
              </div>
              {isLoading ? (
                <div className={classes.empty}>
                  <CircularProgress size={24} />
                </div>
              ) : groups.length === 0 ? (
                <div className={classes.empty}>No history events found.</div>
              ) : (
                <>
                  <div className={classes.groups}>
                    {groups.map((group, index) => (
                      <EntityHistoryTimelineGroup
                        key={group.correlationId}
                        group={group}
                        isLast={index === groups.length - 1}
                      />
                    ))}
                  </div>
                  {hasOlderEntries && (
                    <div className={classes.buttonWrapper}>
                      <Button
                        className={classes.button}
                        variant="outlined"
                        onClick={loadOlder}
                        disabled={isLoadingOlder}
                        size="small"
                      >
                        {isLoadingOlder ? 'Loading...' : 'Load older'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
        {noNewerEventsFound && (
          <Snackbar severity="info" onClose={clearNoNewerEvents}>
            No newer events found
          </Snackbar>
        )}
      </Card>
    </PermissionBoundary>
  );
}
